// strona dev-studio

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  ShieldCheck,
  FileEarmarkText,
  BroadcastPin,
  PlusSquare,
  TagFill,
  Diagram3,
  Trash,
  Plug,
  PencilSquare
} from 'react-bootstrap-icons';

import { ProcessTypeCard, CreateProcessModal, DeleteProcessModal } from 'components/containers';
import { useGetProcesses } from 'api/endpoints/useGetProcesses';
import { useDeleteProcess } from 'api/endpoints/useDeleteProcess';
import { usePostProcess } from 'api/endpoints/usePostProcess';
import { useGetModules, IModule } from 'api/endpoints/useGetModules';
import { usePostModule } from 'api/endpoints/usePostModule';
import { usePutModule } from 'api/endpoints/usePutModule';
import { useGetEntities, IEntity } from 'api/endpoints/useGetEntities';
import { usePostEntity } from 'api/endpoints/usePostEntity';
import { usePutEntity } from 'api/endpoints/usePutEntity';
import { useSnackbar } from 'notistack';

import styles from 'theme/pages/DevStudio/DevStudio.module.scss';

type ModuleModalState = { mode: 'create' } | { mode: 'edit'; module: IModule } | null;
type EntityModalState = { mode: 'create'; moduleUuid: string } | { mode: 'edit'; entity: IEntity } | null;

const DevStudio = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState<{ id: number; name: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'home' | 'workflows' | 'modules'>('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Modules tab state
  const [selectedModuleUuid, setSelectedModuleUuid] = useState<string | null>(null);
  const [moduleModal, setModuleModal] = useState<ModuleModalState>(null);
  const [moduleForm, setModuleForm] = useState({ name: '' });
  const [entityModal, setEntityModal] = useState<EntityModalState>(null);
  const [entityForm, setEntityForm] = useState({ name: '', symbol: '', tableName: '' });

  // Pobieranie listy procesów z API
  const { data: processesData, isLoading } = useGetProcesses({
    search: searchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    page: 1,
    limit: 100
  });

  // Modules & Entities data
  const { data: modules = [], isLoading: isLoadingModules, isFetching: isFetchingModules } = useGetModules({
    enabled: activeTab === 'modules'
  });
  const { data: entities = [], isLoading: isLoadingEntities, isFetching: isFetchingEntities } = useGetEntities(
    selectedModuleUuid,
    { enabled: !!selectedModuleUuid && activeTab === 'modules' }
  );

  // Mutation do tworzenia procesu
  const createMutation = usePostProcess({
    onSuccess: (data) => {
      queryClient.invalidateQueries(['processes']);
      enqueueSnackbar(t('Proces został utworzony pomyślnie'), { variant: 'success' });
      const urlPrefix = `/${i18n.language}`;
      navigate(`${urlPrefix}/dev-studio/editor/${data.id}`);
      handleCloseModal();
    },
    onError: (error) => {
      enqueueSnackbar(t('Błąd podczas tworzenia procesu: {{message}}', { message: error.message || 'Nieznany błąd' }), { variant: 'error' });
    }
  });

  // Mutation do usuwania procesu
  const deleteMutation = useDeleteProcess({
    onSuccess: () => {
      queryClient.invalidateQueries(['processes']);
      enqueueSnackbar(t('Proces został usunięty pomyślnie'), { variant: 'success' });
      setIsDeleteModalOpen(false);
      setProcessToDelete(null);
    },
    onError: (error: any) => {
      enqueueSnackbar(t('Błąd podczas usuwania procesu: {{message}}', { message: error?.message || 'Nieznany błąd' }), { variant: 'error' });
    }
  });

  // Module mutations
  const postModuleMutation = usePostModule({
    onSuccess: () => {
      queryClient.invalidateQueries(['modules']);
      enqueueSnackbar(t('Moduł został utworzony'), { variant: 'success' });
      setModuleModal(null);
    },
    onError: () => { enqueueSnackbar(t('Błąd podczas tworzenia modułu'), { variant: 'error' }); }
  });

  const putModuleMutation = usePutModule({
    onSuccess: () => {
      queryClient.invalidateQueries(['modules']);
      enqueueSnackbar(t('Moduł został zaktualizowany'), { variant: 'success' });
      setModuleModal(null);
    },
    onError: () => { enqueueSnackbar(t('Błąd podczas edycji modułu'), { variant: 'error' }); }
  });

  // Entity mutations
  const postEntityMutation = usePostEntity({
    onSuccess: () => {
      queryClient.invalidateQueries(['entities', selectedModuleUuid]);
      enqueueSnackbar(t('Encja została utworzona'), { variant: 'success' });
      setEntityModal(null);
    },
    onError: () => { enqueueSnackbar(t('Błąd podczas tworzenia encji'), { variant: 'error' }); }
  });

  const putEntityMutation = usePutEntity({
    onSuccess: () => {
      queryClient.invalidateQueries(['entities', selectedModuleUuid]);
      enqueueSnackbar(t('Encja została zaktualizowana'), { variant: 'success' });
      setEntityModal(null);
    },
    onError: () => { enqueueSnackbar(t('Błąd podczas edycji encji'), { variant: 'error' }); }
  });

  const handleCreateWorkflow = (_type?: 'policy' | 'listener' | 'additional_fields' | 'validator' | 'product_label' | 'provider') => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitWorkflow = (data: {
    name: string;
    description: string;
    type: string;
    module_id: number;
    additional_data?: { event_class?: string; policy_kind_tag?: string; policy_subtype_class?: string; additional_fields_service_interface?: string; product_label_field?: string; translations?: Record<string, string>; provider_kind_tag?: string; entity_class?: string };
  }) => {
    createMutation.mutate({
      name: data.name,
      description: data.description || '',
      type: data.type,
      module_id: data.module_id,
      ...(data.additional_data && { additional_data: data.additional_data })
    });
  };

  const handleEditProcess = (processId: number) => {
    const urlPrefix = `/${i18n.language}`;
    navigate(`${urlPrefix}/dev-studio/editor/${processId}`);
  };

  const handleDeleteProcess = (processId: number, processName: string) => {
    setProcessToDelete({ id: processId, name: processName });
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setProcessToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (processToDelete) {
      deleteMutation.mutate({ id: processToDelete.id });
    }
  };

  // Sanityzacja nazwy modułu — usuwa polskie znaki, wymusza PascalCase
  const sanitizeModuleName = (value: string): string => {
    const polishMap: Record<string, string> = {
      ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
      Ą: 'A', Ć: 'C', Ę: 'E', Ł: 'L', Ń: 'N', Ó: 'O', Ś: 'S', Ź: 'Z', Ż: 'Z'
    };
    const cleaned = value
      .split('')
      .map((c) => polishMap[c] ?? c)
      .join('')
      .replace(/[^a-zA-Z0-9\s_-]/g, '');

    return cleaned
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  };

  const sanitizeEntityName = (value: string): string => {
    const polishMap: Record<string, string> = {
      ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
      Ą: 'A', Ć: 'C', Ę: 'E', Ł: 'L', Ń: 'N', Ó: 'O', Ś: 'S', Ź: 'Z', Ż: 'Z'
    };
    // Usuń polskie znaki, podziel na słowa po spacjach/podkreślnikach/myślnikach
    const cleaned = value
      .split('')
      .map((c) => polishMap[c] ?? c)
      .join('')
      .replace(/[^a-zA-Z0-9\s_-]/g, '');

    // Zamień na PascalCase: każde słowo z wielką literą
    return cleaned
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  };

  // Module handlers
  const openCreateModule = () => {
    setModuleForm({ name: '' });
    setModuleModal({ mode: 'create' });
  };

  const openEditModule = (module: IModule) => {
    setModuleForm({ name: module.name });
    setModuleModal({ mode: 'edit', module });
  };

  const submitModuleForm = () => {
    if (!moduleForm.name.trim()) return;
    if (moduleModal?.mode === 'create') {
      postModuleMutation.mutate({ name: moduleForm.name });
    } else if (moduleModal?.mode === 'edit') {
      putModuleMutation.mutate({ id: moduleModal.module.id, name: moduleForm.name });
    }
  };

  // Entity handlers
  const openCreateEntity = () => {
    if (!selectedModuleUuid) return;
    setEntityForm({ name: '', symbol: '', tableName: '' });
    setEntityModal({ mode: 'create', moduleUuid: selectedModuleUuid });
  };

  const openEditEntity = (entity: IEntity) => {
    navigate(`/${i18n.language}/dev-studio/entity/${entity.uuid}`);
  };

  const submitEntityForm = () => {
    if (!entityForm.name.trim()) return;
    if (entityModal?.mode === 'create') {
      postEntityMutation.mutate({
        name: entityForm.name,
        symbol: entityForm.symbol,
        tableName: entityForm.tableName,
        moduleUuid: entityModal.moduleUuid
      });
    } else if (entityModal?.mode === 'edit') {
      putEntityMutation.mutate({
        entityUuid: entityModal.entity.uuid,
        name: entityForm.name,
        symbol: entityForm.symbol,
        tableName: entityForm.tableName
      });
    }
  };

  const workflowTypes = [
    {
      type: 'policy' as const,
      title: t('Polityka'),
      description: t(
        'Polityka - zasady i reguły systemowe. Definiuj zasady działania systemu, reguły dostępu i polityki biznesowe zgodnie z wymaganiami organizacji.'
      ),
      icon: <FileEarmarkText size={48} />
    },
    {
      type: 'listener' as const,
      title: t('Nasłuchiwanie (eventy)'),
      description: t(
        'Nasłuchiwanie - obsługa eventów domenowych. Twórz listenery reagujące na zdarzenia systemowe takie jak utworzenie zamówienia, zmiana klienta i inne.'
      ),
      icon: <BroadcastPin size={48} />
    },
    {
      type: 'additional_fields' as const,
      title: t('Pola dodatkowe (provider)'),
      description: t(
        'Pola dodatkowe - rozszerzanie encji o własne dane. Twórz providery dostarczające dodatkowe pola do encji systemu bez modyfikacji kodu źródłowego.'
      ),
      icon: <PlusSquare size={48} />
    },
    {
      type: 'validator' as const,
      title: t('Walidator'),
      description: t(
        'Walidator - narzędzie do walidacji danych i reguł biznesowych. Twórz zasady weryfikacji danych wejściowych i walidacji zgodności z wymaganiami biznesowymi.'
      ),
      icon: <ShieldCheck size={48} />
    },
    {
      type: 'product_label' as const,
      title: t('Etykieta produktu (AdditionalField)'),
      description: t(
        'Etykieta produktu - dodawanie własnych etykiet do listingu produktów. Definiuj własne pola widoczne na listingu produktów z obsługą tłumaczeń.'
      ),
      icon: <TagFill size={48} />
    },
    {
      type: 'provider' as const,
      title: t('Provider (dostawca danych)'),
      description: t(
        'Provider - dostawca danych agregowany przez TaggedIterator. Twórz providery dostarczające dane zewnętrzne agregowane przez serwisy systemu.'
      ),
      icon: <Plug size={48} />
    }
  ];

  // Dane procesów z API
  const allProcesses = useMemo(() => {
    if (!processesData) return [];
    try {
      const items = processesData.items || [];
      return items.filter(item => item && (item.id !== null && item.id !== undefined));
    } catch (error) {
      return [];
    }
  }, [processesData]);

  const filteredProcesses = allProcesses;

  const stats = useMemo(() => {
    try {
      const activeCount = allProcesses.filter(p => p && p.status === 'active').length;
      const validatorCount = allProcesses.filter(p => p && p.type === 'validator').length;
      const policyCount = allProcesses.filter(p => p && p.type === 'policy').length;
      const listenerCount = allProcesses.filter(p => p && p.type === 'listener').length;

      return [
        { label: t('Aktywne procesy'), value: activeCount.toString(), color: 'primary' },
        { label: t('Walidatory'), value: validatorCount.toString(), color: 'success' },
        { label: t('Polityki'), value: policyCount.toString(), color: 'warning' },
        { label: t('Listenery'), value: listenerCount.toString(), color: 'info' }
      ];
    } catch (error) {
      return [
        { label: t('Aktywne procesy'), value: '0', color: 'primary' },
        { label: t('Walidatory'), value: '0', color: 'success' },
        { label: t('Polityki'), value: '0', color: 'warning' },
        { label: t('Listenery'), value: '0', color: 'info' }
      ];
    }
  }, [allProcesses, t]);

  const recentProcesses = useMemo(() => {
    try {
      return allProcesses
        .filter(p => p && p.id)
        .slice(0, 4)
        .map(p => ({
          name: p.name || '',
          type: p.type || '',
          lastEdit: p.createdAt || null,
          status: p.status || 'inactive'
        }));
    } catch (error) {
      return [];
    }
  }, [allProcesses]);

  const activities = [
    { action: 'Utworzono', item: 'Walidacja zamówień', user: 'Jan Kowalski', time: '2 godz. temu' },
    { action: 'Edytowano', item: 'Proces zatwierdzania', user: 'Anna Nowak', time: '5 godz. temu' },
    { action: 'Opublikowano', item: 'Polityka rabatów', user: 'Piotr Wiśniewski', time: 'Wczoraj' },
    { action: 'Usunięto', item: 'Stary proces', user: 'Maria Kowalczyk', time: '2 dni temu' }
  ];

  const selectedModule = modules.find(m => m.uuid === selectedModuleUuid) ?? null;

  const renderHomeView = () => (
    <>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{t('WiseB2B Development Studio')}</h1>
          <p className={styles.heroDescription}>
            {t(
              'Platforma no-code do rozbudowy logiki biznesowej. Twórz, zarządzaj i wdrażaj procesy biznesowe bez pisania kodu.'
            )}
          </p>
          <div className={styles.heroActions}>
            <button className={styles.primaryButton} onClick={() => handleCreateWorkflow()}>
              {t('Utwórz nowy proces')}
            </button>
            <button className={styles.secondaryButton}>{t('Dokumentacja')}</button>
          </div>
        </div>
        <div className={styles.heroVisual}>
          <div className={styles.floatingCard}>
            <Diagram3 size={64} />
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className={styles.statsSection}>
        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <div className={classnames(styles.statCard, styles[stat.color])}>
                <div className={styles.statValue}>{stat.value}</div>
                <div className={styles.statLabel}>{stat.label}</div>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Main Content Grid */}
      <div className={styles.mainContent}>
        <Grid container spacing={4}>
          {/* Process Types Section - 2/3 width */}
          <Grid item xs={12} lg={8}>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{t('Typy procesów')}</h2>
                <p className={styles.sectionSubtitle}>
                  {t('Wybierz typ procesu aby rozpocząć')}
                </p>
              </div>
              <Grid container spacing={3}>
                {workflowTypes.map((workflowType, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <ProcessTypeCard
                      type={workflowType.type}
                      title={workflowType.title}
                      description={workflowType.description}
                      icon={workflowType.icon}
                      onCreate={handleCreateWorkflow}
                    />
                  </Grid>
                ))}
              </Grid>
            </div>
          </Grid>

          {/* Sidebar - 1/3 width */}
          <Grid item xs={12} lg={4}>
            {/* Recent Processes */}
            <div className={styles.section}>
              <h3 className={styles.sidebarTitle}>{t('Ostatnio edytowane')}</h3>
              <div className={styles.recentList}>
                {recentProcesses.map((process, index) => (
                  <div key={index} className={styles.recentItem}>
                    <div className={styles.recentInfo}>
                      <div className={styles.recentName}>{process.name}</div>
                      <div className={styles.recentMeta}>
                        <span className={styles.recentType}>{process.type}</span>
                        <span className={styles.recentTime}>{process.lastEdit}</span>
                      </div>
                    </div>
                    <div className={classnames(styles.recentStatus, styles[process.status])} />
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Start */}
            <div className={styles.section}>
              <h3 className={styles.sidebarTitle}>{t('Quick Start')}</h3>
              <div className={styles.quickStartList}>
                <div className={styles.quickStartItem}>
                  <div className={styles.quickStartNumber}>1</div>
                  <div className={styles.quickStartText}>{t('Wybierz typ procesu')}</div>
                </div>
                <div className={styles.quickStartItem}>
                  <div className={styles.quickStartNumber}>2</div>
                  <div className={styles.quickStartText}>{t('Nazwij i opisz proces')}</div>
                </div>
                <div className={styles.quickStartItem}>
                  <div className={styles.quickStartNumber}>3</div>
                  <div className={styles.quickStartText}>{t('Projektuj w edytorze')}</div>
                </div>
                <div className={styles.quickStartItem}>
                  <div className={styles.quickStartNumber}>4</div>
                  <div className={styles.quickStartText}>{t('Testuj i wdrażaj')}</div>
                </div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div className={styles.section}>
              <h3 className={styles.sidebarTitle}>{t('Aktywność')}</h3>
              <div className={styles.timeline}>
                {activities.map((activity, index) => (
                  <div key={index} className={styles.timelineItem}>
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineAction}>
                        <strong>{activity.action}</strong> {activity.item}
                      </div>
                      <div className={styles.timelineMeta}>
                        {activity.user} • {activity.time}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );

  const renderWorkflowsView = () => (
    <div className={styles.workflowsView}>
      <div className={styles.workflowsHeader}>
        <div className={styles.workflowsTitleRow}>
          <h2 className={styles.workflowsTitle}>{t('Wszystkie procesy')}</h2>
          <button
            className={styles.addWorkflowButton}
            onClick={() => handleCreateWorkflow()}>
            {t('Dodaj nowy proces')}
          </button>
        </div>
        <div className={styles.workflowsControls}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder={t('Szukaj procesu...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.filterButtons}>
            <button
              className={classnames(styles.filterButton, {
                [styles.active]: statusFilter === 'all'
              })}
              onClick={() => setStatusFilter('all')}>
              {t('Wszystkie')}
            </button>
            <button
              className={classnames(styles.filterButton, {
                [styles.active]: statusFilter === 'active'
              })}
              onClick={() => setStatusFilter('active')}>
              {t('Aktywne')}
            </button>
            <button
              className={classnames(styles.filterButton, {
                [styles.active]: statusFilter === 'inactive'
              })}
              onClick={() => setStatusFilter('inactive')}>
              {t('Nieaktywne')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.workflowsTable}>
        <div className={styles.tableHeader}>
          <div className={styles.tableCell}>{t('Nazwa')}</div>
          <div className={styles.tableCell}>{t('Typ')}</div>
          <div className={styles.tableCell}>{t('Status')}</div>
          <div className={styles.tableCell}>{t('Akcje')}</div>
        </div>
        <div className={styles.tableBody}>
          {isLoading ? (
            <div className={styles.tableRow}>
              <div className={styles.tableCell} style={{ gridColumn: '1 / -1' }}>
                {t('Ładowanie...')}
              </div>
            </div>
          ) : filteredProcesses.length === 0 && !isLoading ? (
            <div className={styles.tableRow}>
              <div className={styles.tableCell} style={{ gridColumn: '1 / -1' }}>
                {t('Brak procesów')}
              </div>
            </div>
          ) : (
            filteredProcesses.map((process) => {
              if (!process || !process.id) return null;
              return (
                <div key={process.id} className={styles.tableRow}>
                  <div className={styles.tableCell} data-label={t('Nazwa')}>
                    <a
                      className={styles.processName}
                      href={`/${i18n.language}/dev-studio/editor/${process.id}`}
                      onClick={(e) => { e.preventDefault(); handleEditProcess(process.id); }}>
                      {process.name || '-'}
                    </a>
                  </div>
                  <div className={styles.tableCell} data-label={t('Typ')}>
                    <span className={styles.typeBadge}>{process.type || '-'}</span>
                  </div>
                  <div className={styles.tableCell} data-label={t('Status')}>
                    <span
                      className={classnames(styles.statusBadge, {
                        [styles.active]: process.status === 'active',
                        [styles.inactive]: process.status !== 'active'
                      })}>
                      {process.status === 'active' ? t('Aktywny') : t('Nieaktywny')}
                    </span>
                  </div>
                  <div className={styles.tableCell} data-label={t('Akcje')}>
                    <div className={styles.actionButtons}>
                      <a
                        className={styles.actionButton}
                        href={`/${i18n.language}/dev-studio/editor/${process.id}`}
                        onClick={(e) => { e.preventDefault(); handleEditProcess(process.id); }}>
                        {t('Edytuj')}
                      </a>
                      <button
                        className={styles.actionButton}
                        onClick={() => handleDeleteProcess(process.id, process.name || '')}
                        disabled={deleteMutation.isLoading}>
                        <Trash size={14} />
                        {t('Usuń')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            }).filter(Boolean)
          )}
        </div>
      </div>
    </div>
  );

  const renderModulesView = () => (
    <div className={styles.modulesView}>
      <div className={styles.modulesHeader}>
        <h2>{t('Moduły i encje')}</h2>
        <button className={styles.addButton} onClick={openCreateModule}>
          + {t('Nowy moduł')}
        </button>
      </div>

      <div className={styles.modulesLayout}>
        {/* Lista modułów */}
        <div className={styles.modulesList}>
          <div className={styles.modulesListHeader}>
            <h3>{t('Moduły')}</h3>
          </div>
          {(isLoadingModules || isFetchingModules) ? (
            <div className={styles.modulesSkeleton}>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles.moduleSkeletonItem} />
              ))}
            </div>
          ) : modules.length === 0 ? (
            <div className={styles.emptyModules}>{t('Brak modułów')}</div>
          ) : (
            modules.map((module) => (
              <div
                key={module.uuid}
                className={classnames(styles.moduleItem, {
                  [styles.activeModule]: selectedModuleUuid === module.uuid
                })}
                onClick={() => setSelectedModuleUuid(module.uuid)}>
                <span className={styles.moduleName}>{module.name}</span>
                <div className={styles.moduleActions}>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditModule(module); }}
                    title={t('Edytuj')}>
                    <PencilSquare size={12} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Panel encji */}
        <div className={styles.entitiesPanel}>
          {!selectedModuleUuid ? (
            <div className={styles.selectModuleHint}>
              <p>{t('Wybierz moduł aby zobaczyć jego encje')}</p>
            </div>
          ) : (
            <>
              <div className={styles.entitiesHeader}>
                <h3>{t('Encje modułu')} {selectedModule?.name}</h3>
                <button className={styles.addButton} onClick={openCreateEntity}>
                  + {t('Nowa encja')}
                </button>
              </div>
              <div className={styles.entitiesTable}>
                <div className={styles.entitiesTableHeader}>
                  <span>{t('Nazwa')}</span>
                  <span>{t('Symbol')}</span>
                  <span>{t('Tabela')}</span>
                  <span>{t('Akcje')}</span>
                </div>
                {(isLoadingEntities || isFetchingEntities) ? (
                  <div className={styles.modulesSkeleton}>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={styles.moduleSkeletonItem} />
                    ))}
                  </div>
                ) : entities.length === 0 ? (
                  <div className={styles.emptyEntities}>{t('Brak encji w tym module')}</div>
                ) : (
                  entities.map((entity) => (
                    <div key={entity.uuid} className={styles.entityRow}>
                      <span className={styles.entityName}>{entity.name}</span>
                      <span className={styles.entitySymbol}>{entity.symbol}</span>
                      <span className={styles.entityTable}>{entity.tableName}</span>
                      <div className={styles.entityRowActions}>
                        <button
                          className={styles.editButton}
                          onClick={() => openEditEntity(entity)}>
                          <PencilSquare size={12} /> {t('Edytuj')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal modułu */}
      {moduleModal && (
        <div className={styles.moduleModal}>
          <div className={styles.modalBackdrop} onClick={() => setModuleModal(null)} />
          <div className={styles.modalBox}>
            <h3>{moduleModal.mode === 'create' ? t('Nowy moduł') : t('Edytuj moduł')}</h3>
            <div className={styles.modalField}>
              <label>{t('Nazwa modułu')}</label>
              <input
                type="text"
                value={moduleForm.name}
                onChange={(e) => setModuleForm({ name: sanitizeModuleName(e.target.value) })}
                placeholder="np. myModule"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && submitModuleForm()}
              />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={() => setModuleModal(null)}>
                {t('Anuluj')}
              </button>
              <button
                className={styles.submitButton}
                onClick={submitModuleForm}
                disabled={!moduleForm.name.trim() || postModuleMutation.isLoading || putModuleMutation.isLoading}>
                {t('Zapisz')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal encji */}
      {entityModal && (
        <div className={styles.moduleModal}>
          <div className={styles.modalBackdrop} onClick={() => setEntityModal(null)} />
          <div className={styles.modalBox}>
            <h3>{entityModal.mode === 'create' ? t('Nowa encja') : t('Edytuj encję')}</h3>
            <div className={styles.modalField}>
              <label>{t('Nazwa encji')}</label>
              <input
                type="text"
                value={entityForm.name}
                onChange={(e) => setEntityForm(f => ({ ...f, name: sanitizeEntityName(e.target.value) }))}
                placeholder="np. Complain"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && submitEntityForm()}
              />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={() => setEntityModal(null)}>
                {t('Anuluj')}
              </button>
              <button
                className={styles.submitButton}
                onClick={submitEntityForm}
                disabled={!entityForm.name.trim() || postEntityMutation.isLoading || putEntityMutation.isLoading}>
                {t('Zapisz')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-DevStudio')}>
      {/* Navigation Tabs */}
      <div className={styles.navigationTabs}>
        <button
          className={classnames(styles.navTab, { [styles.active]: activeTab === 'home' })}
          onClick={() => setActiveTab('home')}>
          {t('Strona główna')}
        </button>
        <button
          className={classnames(styles.navTab, { [styles.active]: activeTab === 'workflows' })}
          onClick={() => setActiveTab('workflows')}>
          {t('Wszystkie procesy')}
        </button>
        <button
          className={classnames(styles.navTab, { [styles.active]: activeTab === 'modules' })}
          onClick={() => setActiveTab('modules')}>
          {t('Moduły')}
        </button>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'home' && renderHomeView()}
      {activeTab === 'workflows' && renderWorkflowsView()}
      {activeTab === 'modules' && renderModulesView()}

      <CreateProcessModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitWorkflow}
      />

      <DeleteProcessModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        processName={processToDelete?.name || ''}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default DevStudio;
